import clientApi from '../../../api/clientApi';
import { Endpoints } from '../../../utility/constants';
import { showToast } from '../../../toast';
import { history } from '../../../history';
import { removeStorage, setStorageObj, getStorageValue } from '../../../utility/helpers';
import { handleError, getRoList } from '../app';

const subdomainWhitelist = [
  'docker',
  'api-isa1',
  'api-isa2',
  'api-isa3',
  'api-isa4',
  'api-isa5',
  'api-isa6',
];

/**
 * Restores app user from storage to Redux.
 * Called on page refresh when already authenticated.
 */
export const restoreAppUser = () => async (dispatch) => {
  try {
    const userJson = await getStorageValue('iservice_technician_app_user');
    if (userJson) {
      const user = JSON.parse(userJson);
      dispatch({ type: 'APP_LOGGED_IN', data: user });
      return user;
    }
    return null;
  } catch (error) {
    console.error('Failed to restore app user:', error);
    return null;
  }
};

/**
 * Restores subdomain from storage to Redux.
 * Called on page refresh when already authenticated.
 */
export const restoreSubdomain = () => async (dispatch) => {
  try {
    const subdomain = await getStorageValue('iservice_technician_subdomain');
    if (subdomain) {
      dispatch({ type: 'SET_SUBDOMAIN', data: subdomain });
      return subdomain;
    }
    return null;
  } catch (error) {
    console.error('Failed to restore subdomain:', error);
    return null;
  }
};

/**
 * Loads settings from API and stores in Redux.
 * Called after app login and on page refresh when already authenticated.
 * This is the minimal settings load needed for Product Fruits initialization.
 */
export const loadSettings = () => async (dispatch) => {
  try {
    const settingsResponse = await clientApi.get(`${Endpoints.settings}`);
    const settings = settingsResponse.data.reduce((acc, setting) => {
      acc[setting.key] = setting.value;
      return acc;
    }, {});

    dispatch({
      type: 'RECEIVE_SETTINGS',
      data: settings,
    });

    return settings;
  } catch (error) {
    dispatch(handleError(error));
    return null;
  }
};

const loadLaborRates = () => async (dispatch) => {
  try {
    const response = await clientApi.get(`${Endpoints.laborRates}`);
    dispatch({
      type: 'RECEIVE_LABOR_RATES',
      data: response.data.results,
    });
    return response.data.results;
  } catch (error) {
    dispatch(handleError(error));
    return null;
  }
};

const loadPriceMatrix = () => async (dispatch, getState) => {
  const { settings } = getState().app;
  const useMatrix =
    settings.pricingUseMatrix === 'true' ||
    settings.pricingUseMatrix === '1' ||
    settings.pricingUseMatrix === true;

  if (!useMatrix) return null;

  try {
    const response = await clientApi.get(Endpoints.priceMatrix);
    dispatch({
      type: 'RECEIVE_PRICE_MATRIX',
      data: response.data,
    });
    return response.data;
  } catch (error) {
    dispatch(handleError(error));
    return null;
  }
};

const loadDefaultLaborRate = () => async (dispatch, getState) => {
  const { settings } = getState().app;

  if (settings.quote !== '1') return null;

  try {
    const defaultLaborRateId = parseInt(settings.defaultLaborRateId);
    if (!defaultLaborRateId) {
      throw new Error('Please set a default Pay Type');
    }

    const response = await clientApi.get(`${Endpoints.laborRates}/${defaultLaborRateId}`);
    if (!response.data) {
      throw new Error(
        'Something went wrong finding the default Pay Type. Please reopen the app or contact Support.',
      );
    }

    // Add defaultLaborRate to settings
    dispatch({
      type: 'RECEIVE_SETTINGS',
      data: { ...settings, defaultLaborRate: response.data },
    });

    return response.data;
  } catch (error) {
    dispatch(handleError(error));
    return null;
  }
};

/**
 * Initialize data needed after tech login (labor rates, price matrix, default labor rate).
 * Settings should already be loaded by loadSettings.
 */
const initTechData = () => async (dispatch, getState) => {
  const { settings } = getState().app;

  // Ensure settings are loaded first
  if (!settings || Object.keys(settings).length === 0) {
    await dispatch(loadSettings());
  }

  await dispatch(loadLaborRates());
  await dispatch(loadPriceMatrix());
  await dispatch(loadDefaultLaborRate());
};

export const changeRole = (role) => {
  return (dispatch) => dispatch({ type: 'CHANGE_ROLE', userRole: role });
};

export const logoutApp = () => async (dispatch) => {
  removeStorage('iservice_technician_app_token');
  removeStorage('iservice_technician_tech_token');
  removeStorage('iservice_technician_tech_username');

  dispatch({ type: 'LOGOUT_APP' });
};

export const logoutTech = () => async (dispatch) => {
  removeStorage('iservice_technician_tech_token');
  removeStorage('iservice_technician_tech_username');
  dispatch({ type: 'TECH_LOGGED_OUT' });
  history.push('/');
  showToast('Technician logged out');
};

export const loginApp = (username, password, accountCode) => async (dispatch) => {
  dispatch({ type: 'LOADING' });
  const credentials = { username, password };

  const subdomain = addSubdomainPrefix(accountCode);
  await setStorageObj('iservice_technician_subdomain', subdomain);
  dispatch({ type: 'SET_SUBDOMAIN', data: subdomain });

  try {
    const response = await clientApi.post(Endpoints.authentication, credentials);

    await removeStorage('iservice_technician_tech_token');
    await removeStorage('iservice_technician_tech_username');
    await setStorageObj('iservice_technician_app_token', response.data.token);
    await setStorageObj('iservice_technician_app_user', JSON.stringify(response.data.user));

    dispatch({ type: 'APP_LOGGED_IN', data: response.data.user });

    // Load settings for Product Fruits initialization
    await dispatch(loadSettings());

    return accountCode;
  } catch (error) {
    dispatch({ type: 'LOADING_END' });
    dispatch(handleError(error));

    // On failed login, the accountCode has effectively been validated
    if (error.response?.status === 403 || error.response?.status === 401) {
      return accountCode;
    }

    return null;
  }
};

export const loginTech = (tech, pin) => async (dispatch) => {
  dispatch({ type: 'LOADING' });

  try {
    let username = tech?.username || tech?.email;
    if (!username) {
      username = await getStorageValue('iservice_technician_tech_username');
    }

    const response = await clientApi.post(Endpoints.authentication, {
      username,
      pin,
    });

    setStorageObj('iservice_technician_tech_token', response.data.token);
    setStorageObj('iservice_technician_tech_username', username);

    dispatch({ type: 'TECH_LOGGED_IN', data: response.data.user });
    await dispatch(initTechData());
    dispatch(getRoList({ pageLimit: 20 }));

    return true;
  } catch (error) {
    return dispatch(handleError(error));
  } finally {
    dispatch({ type: 'LOADING_END' });
  }
};

const addSubdomainPrefix = (subdomain) => {
  const prefix = 'api.';
  // Only skip prefix if it already starts with 'api.' or is in whitelist
  return subdomain.startsWith(prefix) || subdomainWhitelist.includes(subdomain)
    ? subdomain
    : `${prefix}${subdomain}`;
};
